import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../auth/AuthContext";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { ArrowLeft, Send, Mic, Square, Volume2, VolumeX, Sparkles, Telescope, Radio } from "lucide-react";
import { toast } from "sonner";
import DeepResearchDialog from "../components/DeepResearchDialog";
import TrustLine from "../components/TrustLine";
import { resolveFirstName } from "../lib/firstName";
import { MentorStyleSwitcher } from "../components/MentorStyleSwitcher";
import { TYPING_BRIDGE_PHRASES } from "../lib/typingBridge";

export default function CoachPage() {
  const { user, onboarding } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const voiceMode = new URLSearchParams(location.search).get("voice") === "1";

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [speaking, setSpeaking] = useState(false);
  const [recording, setRecording] = useState(false);
  const [autoSpeak, setAutoSpeak] = useState(voiceMode);
  const [researchOpen, setResearchOpen] = useState(false);
  const [researchEnabled, setResearchEnabled] = useState(false);
  // Local override of the coach style for instant UI feedback when the user
  // picks a new mentor tone — falls back to the persisted onboarding value.
  const [styleOverride, setStyleOverride] = useState(null);
  const activeStyle = styleOverride || onboarding?.coach_style;
  // Typing-bridge rotation tick: bumps every ~2.5s while sending.
  const [bridgeTick, setBridgeTick] = useState(0);

  const audioRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const scrollRef = useRef(null);

  // Hide Deep Research unless the backend has a Tavily key configured.
  useEffect(() => {
    axios
      .get(`${API}/config`)
      .then((r) => setResearchEnabled(!!r.data?.tavily_configured))
      .catch(() => setResearchEnabled(false));
  }, []);

  useEffect(() => {
    // Seed greeting if no messages
    if (messages.length === 0) {
      const coachStyle = onboarding?.coach_style || "calm";
      const firstName = onboarding?.preferred_name || resolveFirstName(user, "there");
      const greeting = {
        calm: `Hi ${firstName}. Take a breath. What's on your mind today — even the small thing?`,
        hard: `${firstName}. Let's skip the warm-up. What's the thing you've been avoiding?`,
        nobs: `${firstName} — tell me straight. What do you actually need to deal with right now?`,
        friend: `Hey ${firstName}! How are you really doing today?`,
      };
      setMessages([{ role: "assistant", content: greeting[coachStyle] || greeting.calm }]);
    }
    // If the user just clicked "Today's check-in" (or another deep-link),
    // we have a one-shot prefilled message in sessionStorage. Auto-send it
    // so the mentor leads with substance instead of waiting silently.
    const prefill = typeof window !== "undefined" && sessionStorage.getItem("ascend_coach_prefill");
    if (prefill) {
      sessionStorage.removeItem("ascend_coach_prefill");
      // Slight delay so the greeting renders first.
      setTimeout(() => send(prefill), 350);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  // Rotate the typing-bridge phrase every 2.5s while a reply is pending.
  useEffect(() => {
    if (!sending) {
      setBridgeTick(0);
      return;
    }
    const id = setInterval(() => setBridgeTick((t) => t + 1), 2500);
    return () => clearInterval(id);
  }, [sending]);

  // Typewriter animation state: index of character to reveal for message[typingIdx]
  // of the latest assistant message. When a new assistant message arrives, we
  // start it at 0 and ramp to full length at ~22ms/char — ChatGPT-style streaming
  // without a real SSE stream.
  const [typedChars, setTypedChars] = useState({}); // {messageIndex: charsRevealed}

  useEffect(() => {
    const last = messages[messages.length - 1];
    if (!last || last.role !== "assistant" || last._error) return;
    const lastIdx = messages.length - 1;
    if (typedChars[lastIdx] >= (last.content || "").length) return;
    const target = (last.content || "").length;
    const start = typedChars[lastIdx] || 0;
    if (start >= target) return;
    // Reveal ~45 chars per second — fast enough to feel instant, slow enough
    // to feel human. Finish within 1.5s for a 3-sentence reply.
    let current = start;
    const id = setInterval(() => {
      // Reveal a small random chunk each tick (3-6 chars) for a more organic cadence.
      current = Math.min(target, current + 3 + Math.floor(Math.random() * 4));
      setTypedChars((prev) => ({ ...prev, [lastIdx]: current }));
      if (current >= target) clearInterval(id);
    }, 22);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [messages.length]);

  // Voice-loop mode: when active, after TTS finishes playing, auto-rearm the mic
  // so the user can speak the next message without tapping anything.
  const [voiceLoopActive, setVoiceLoopActive] = useState(false);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content }]);
    setSending(true);
    try {
      const r = await axios.post(
        `${API}/chat/send`,
        { message: content, session_id: sessionId },
        { timeout: 45000 } // 45s hard timeout — never leave the user staring
      );
      setSessionId(r.data.session_id);
      const reply = r.data.reply;
      if (!reply || typeof reply !== "string") {
        throw new Error("empty_reply");
      }
      setMessages((m) => [...m, { role: "assistant", content: reply }]);
      // Surface coach's roadmap actions as toasts
      const actions = r.data.actions || [];
      actions.forEach((a) => {
        if (a.type === "add_milestone") toast.success(`Coach added: ${a.milestone?.title || "new milestone"}`);
        else if (a.type === "move_milestone") toast.success("Coach moved a milestone.");
        else if (a.type === "complete_milestone") toast.success("Coach marked a milestone complete.");
        else if (a.type === "delete_milestone") toast.success("Coach removed a milestone.");
      });
      if (autoSpeak) {
        speakText(reply);
      }
    } catch (e) {
      console.error("[/chat/send] error:", e?.response?.status, e?.response?.data, e?.message);
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      // Show the actual problem inline as a coach message so the user is never
      // left staring at silence. This replaces the silent-failure that made
      // people think "the mentor doesn't respond at all".
      let inlineMsg;
      if (status === 401 || status === 403) {
        inlineMsg = "Your session may have timed out. Tap the back arrow to return to your dashboard and try again.";
      } else if (status === 429) {
        inlineMsg = "Too many messages too fast — give me a sec and try again.";
      } else if (status === 402) {
        inlineMsg = detail || "Your trial may have ended — visit Pricing to continue talking.";
      } else if (e?.code === "ECONNABORTED" || (e?.message || "").includes("timeout")) {
        inlineMsg = "That took longer than expected. Try once more — I'm here.";
      } else if (typeof detail === "string") {
        inlineMsg = detail;
      } else {
        inlineMsg = "Something glitched on my end. Can you say that again?";
      }
      setMessages((m) => [...m, { role: "assistant", content: inlineMsg, _error: true }]);
      toast.error("Coach didn't respond. Try again.");
    } finally {
      setSending(false);
    }
  };

  const speakText = async (text) => {
    try {
      setSpeaking(true);
      const token = localStorage.getItem("ascend_session_token");
      const resp = await fetch(`${API}/voice/tts`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ text }),
      });
      if (!resp.ok) {
        if (resp.status === 402) {
          toast.error("Voice coaching is in Advanced.");
        }
        throw new Error("TTS failed");
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      if (audioRef.current) {
        audioRef.current.src = url;
        audioRef.current.onended = () => {
          setSpeaking(false);
          // Voice-loop mode: as soon as the coach finishes speaking, re-arm
          // the mic so the user can respond without tapping anything.
          if (voiceLoopActive) {
            setTimeout(() => {
              if (!recording) startRecording();
            }, 400);
          }
        };
        await audioRef.current.play();
      }
    } catch (e) {
      console.error(e);
      setSpeaking(false);
      toast.error("Voice playback failed.");
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const form = new FormData();
        form.append("audio", blob, "speech.webm");
        try {
          // Let axios auto-set multipart/form-data with boundary; manually
          // setting it without a boundary breaks multipart parsing on the server.
          const r = await axios.post(`${API}/voice/stt`, form);
          const transcript = r.data.text || r.data.transcript || "";
          if (transcript.trim()) {
            send(transcript);
          } else {
            toast.error("Didn't catch that.");
          }
        } catch (e) {
          const detail = e?.response?.data?.detail || "Transcription failed.";
          toast.error(detail, { duration: 5000 });
        }
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch (e) {
      toast.error("Microphone access denied.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const stopSpeaking = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setSpeaking(false);
  };

  return (
    <div className="min-h-screen bg-[#f7f3ea] flex flex-col">
      <header className="sticky top-0 z-40 bg-[#f7f3ea]/85 backdrop-blur-md border-b border-[#e9e0cd]">
        <div className="max-w-4xl mx-auto px-6 h-[68px] flex items-center justify-between">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 font-body text-[14px] text-[#2b3442] hover:text-[#486a8f]"
          >
            <ArrowLeft size={16} /> Dashboard
          </Link>
          <div className="flex items-center gap-2">
            {researchEnabled && (
              <Button
                data-testid="coach-deep-research-btn"
                variant="ghost"
                onClick={() => setResearchOpen(true)}
                className="rounded-full text-[13px] text-[#0f1724] hover:bg-[#f2ecdc] inline-flex items-center"
              >
                <Telescope size={14} className="mr-2 text-[#c8b88a]" />
                Deep Research
              </Button>
            )}
            <Button
              data-testid="coach-voice-toggle-btn"
              aria-label={`Voice ${autoSpeak ? "on" : "off"}`}
              variant="ghost"
              onClick={() => {
                const next = !autoSpeak;
                setAutoSpeak(next);
                // Turning voice off also exits hands-free mode.
                if (!next) setVoiceLoopActive(false);
              }}
              className="rounded-full text-[13px] text-[#2b3442] hover:bg-[#f2ecdc]"
            >
              {autoSpeak ? <Volume2 size={14} className="mr-2 text-[#486a8f]" /> : <VolumeX size={14} className="mr-2" />}
              Voice {autoSpeak ? "on" : "off"}
            </Button>
            <Button
              data-testid="coach-handsfree-toggle-btn"
              aria-label={`Hands-free ${voiceLoopActive ? "on" : "off"}`}
              variant="ghost"
              onClick={() => {
                const next = !voiceLoopActive;
                setVoiceLoopActive(next);
                // Hands-free REQUIRES voice on — turning it on turns on voice.
                if (next) setAutoSpeak(true);
                if (next) toast.success("Hands-free on. Mic will auto-rearm after each response.", { duration: 4000 });
                else toast.info("Hands-free off.");
              }}
              className={`rounded-full text-[13px] hover:bg-[#f2ecdc] ${voiceLoopActive ? "text-[#3e6b52] bg-[#e5f0e7]" : "text-[#2b3442]"}`}
            >
              <Radio size={14} className={`mr-2 ${voiceLoopActive ? "text-[#3e6b52]" : ""}`} />
              {voiceLoopActive ? "Hands-free" : "Hands-free"}
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1 w-full max-w-4xl mx-auto px-6 py-8 flex flex-col">
        <div className="mb-6">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#e6dcc6]">
              <Sparkles size={12} className="text-[#486a8f]" />
              <span className="font-mono-eyebrow text-[#486a8f]">
                Your mentor
              </span>
            </div>
            <MentorStyleSwitcher
              currentStyle={activeStyle}
              onChange={(k) => setStyleOverride(k)}
            />
          </div>
          <h1 className="font-display text-[30px] md:text-[38px] leading-tight text-[#0f1724] tracking-tight">
            Talk to your{" "}
            <span className="italic font-light text-[#486a8f]">coach</span>.
          </h1>
          <div className="mt-2 flex items-center gap-3 flex-wrap">
            <TrustLine className="" />
            <Link
              to="/coach/voice"
              data-testid="coach-switch-to-voice-link"
              className="inline-flex items-center gap-1 font-body text-[13px] text-[#486a8f] hover:text-[#3a5a7c] underline underline-offset-2"
            >
              <Mic size={12} /> Or talk by voice
            </Link>
          </div>
        </div>

        {/* Messages */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto rounded-2xl bg-white border border-[#eadfc9] p-6 md:p-8 mb-4 space-y-5 min-h-[400px] max-h-[60vh]"
        >
          {messages.map((m, i) => {
            const isLatestAssistant = m.role === "assistant" && !m._error && i === messages.length - 1;
            const revealed = isLatestAssistant ? (typedChars[i] ?? (m.content || "").length) : (m.content || "").length;
            const shown = (m.content || "").slice(0, revealed);
            const stillTyping = isLatestAssistant && revealed < (m.content || "").length;
            return (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-5 py-3.5 ${
                    m.role === "user"
                      ? "bg-[#486a8f] text-white rounded-br-md"
                      : "bg-[#faf6ec] text-[#0f1724] border border-[#f2ecdc] rounded-bl-md"
                  }`}
                >
                  <p className="font-body text-[15px] leading-relaxed whitespace-pre-wrap">
                    {shown}
                    {stillTyping && <span className="inline-block w-[2px] h-[14px] bg-[#486a8f] ml-0.5 align-middle animate-pulse" />}
                  </p>
                </div>
              </div>
            );
          })}
          {sending && (
            <div className="flex justify-start" data-testid="coach-typing-bridge">
              <div className="bg-[#faf6ec] border border-[#f2ecdc] rounded-2xl rounded-bl-md px-5 py-3.5">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#486a8f] animate-pulse" />
                  <span className="w-2 h-2 rounded-full bg-[#486a8f] animate-pulse" style={{ animationDelay: "0.2s" }} />
                  <span className="w-2 h-2 rounded-full bg-[#486a8f] animate-pulse" style={{ animationDelay: "0.4s" }} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Composer */}
        <div className="rounded-2xl bg-white border border-[#eadfc9] p-3 flex items-end gap-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder="Type what's on your mind…"
            className="flex-1 resize-none min-h-[50px] max-h-[160px] border-0 focus-visible:ring-0 font-body text-[15px]"
          />
          <div className="flex gap-2 self-end">
            {!recording ? (
              <Button
                data-testid="coach-mic-record-btn"
                aria-label="Record voice message"
                onClick={startRecording}
                size="icon"
                className="h-11 w-11 rounded-full bg-[#f2ecdc] hover:bg-[#e9e0cd] text-[#486a8f]"
                title="Hold to speak"
              >
                <Mic size={17} />
              </Button>
            ) : (
              <Button
                data-testid="coach-mic-stop-btn"
                aria-label="Stop recording"
                onClick={stopRecording}
                size="icon"
                className="h-11 w-11 rounded-full bg-[#b94e4e] hover:bg-[#a33f3f] text-white animate-pulse"
              >
                <Square size={15} fill="currentColor" />
              </Button>
            )}
            <Button
              data-testid="coach-send-btn"
              aria-label="Send message"
              onClick={() => send()}
              disabled={!input.trim() || sending}
              size="icon"
              className="h-11 w-11 rounded-full bg-[#486a8f] hover:bg-[#3a5a7c] text-white btn-lift"
            >
              <Send size={16} />
            </Button>
          </div>
        </div>

        {speaking && (
          <div className="mt-3 flex items-center justify-center">
            <button
              onClick={stopSpeaking}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#e6dcc6] font-body text-[12px] text-[#2b3442] hover:bg-[#faf6ec]"
            >
              <span className="w-2 h-2 rounded-full bg-[#486a8f] pulse-dot" />
              Coach is speaking · tap to stop
            </button>
          </div>
        )}

        <audio ref={audioRef} hidden />
      </main>
      <DeepResearchDialog
        open={researchOpen}
        onClose={() => setResearchOpen(false)}
        onUpgrade={() => navigate("/pricing")}
      />
    </div>
  );
}
